/**
 * Blog — réplica del backoffice de Skeleton.
 *   - Tabs: Todos / Borrador / Publicados / Archivados
 *   - Botón "+ Nuevo Artículo"
 *   - Lista con cover + título + excerpt + status + fecha
 *   - Click en post → editor con bloques
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { Plus, FileText, Pencil, Trash2, Eye } from 'lucide-react'

export const Route = createFileRoute('/store/$id/blog/')({
  component: BlogIndex,
})

interface BlogPost {
  id: string
  slug: string
  coverImage: string | null
  status: 'draft' | 'published' | 'archived'
  publishedAt: string | null
  title: string
  excerpt: string | null
  createdAt: string
  updatedAt: string
}

interface BlogResponse {
  data: BlogPost[]
  stats: { total: number; draft: number; published: number; archived: number }
}

type Tab = 'all' | 'draft' | 'published' | 'archived'

function BlogIndex() {
  const { id } = Route.useParams()
  const { activeTenantId } = useAuth()
  const [tab, setTab] = useState<Tab>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['blog', tab],
    queryFn: () => tenantApi.get<BlogResponse>(
      tab === 'all' ? '/api/v1/blog' : `/api/v1/blog?status=${tab}`,
    ),
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []

  return (
    <>
      <PageHeader
        title="Blog"
        description="Artículos del blog"
        action={
          <Link to="/store/$id/blog/$postId" params={{ id, postId: 'new' }}>
            <Button size="sm">
              <Plus className="size-4 mr-2" /> Nuevo Artículo
            </Button>
          </Link>
        }
      />

      <div className="p-8 space-y-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(['all', 'draft', 'published', 'archived'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                tab === t ? 'bg-[hsl(var(--muted))] text-foreground border-border' : 'bg-transparent text-muted-foreground border-transparent hover:text-foreground',
              )}
            >
              {t === 'all' ? 'Todos' : t === 'draft' ? 'Borrador' : t === 'published' ? 'Publicados' : 'Archivados'}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <FileText className="size-12 text-muted-foreground mb-3" />
            <p className="text-base font-medium">No hay artículos</p>
            <Link to="/store/$id/blog/$postId" params={{ id, postId: 'new' }}>
              <Button size="sm" className="mt-3">
                <Plus className="size-4 mr-2" /> Crear primer artículo
              </Button>
            </Link>
          </Card>
        )}

        {list.length > 0 && (
          <div className="space-y-3">
            {list.map((p) => <PostRow key={p.id} post={p} tenantId={id} />)}
          </div>
        )}
      </div>
    </>
  )
}

function PostRow({ post, tenantId }: { post: BlogPost; tenantId: string }) {
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: () => tenantApi.del(`/api/v1/blog/${post.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blog'] }),
  })

  const statusVariant = post.status === 'published' ? 'success' : post.status === 'draft' ? 'warning' : 'muted'
  const statusLabel = post.status === 'published' ? 'Publicado' : post.status === 'draft' ? 'Borrador' : 'Archivado'

  return (
    <Card className="p-4 flex items-center gap-4 hover:border-border transition">
      <div className="size-16 rounded-md border border-border overflow-hidden bg-background shrink-0">
        {post.coverImage ? (
          <img src={post.coverImage} alt={post.title} className="size-full object-cover" />
        ) : (
          <div className="size-full flex items-center justify-center text-muted-foreground">
            <FileText className="size-6" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold truncate">{post.title || post.slug}</p>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
        </div>
        {post.excerpt && <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{post.excerpt}</p>}
        <p className="text-xs text-muted-foreground font-mono mt-1">/{post.slug}</p>
      </div>
      <div className="text-xs text-muted-foreground shrink-0 text-right">
        {post.publishedAt
          ? new Date(post.publishedAt).toLocaleDateString('es-CO')
          : new Date(post.updatedAt).toLocaleDateString('es-CO')}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Link to="/store/$id/blog/$postId" params={{ id: tenantId, postId: post.id }}>
          <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]" title="Editar">
            <Pencil className="size-4" />
          </button>
        </Link>
        <button
          onClick={() => { if (confirm('¿Eliminar este post?')) remove.mutate() }}
          className="p-1.5 rounded text-red-700 dark:text-red-400 hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40"
          title="Eliminar"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </Card>
  )
}
